import { Autocomplete, Grid, Skeleton, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocalization } from '@/hooks/useLocalization';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import {
  DiversificationCategory,
  DiversificationScheme,
  useGetDiversificationCategoriesQuery,
} from '@/store/diversificationsApi';
import { useGetAdvisorGroupSettingsQuery } from '@/store/settingsApi';
import AdvisorGroupSettings from '@/utils/advisorGroupSettings.utility';

import { ClassificationItem } from '../categories.utility';
import { RealEstateDetailsFormState } from '../ManualRealEstateDetails/useRealEstateDetailsForm';
import usePortfolioDiversificationCategories from './usePortfolioDiversificationCategories';
import { sortAndDeduplicateByName } from './diversificationOptions.utility';
import { categoryUsesTreeRelationships } from './diversificationTree.utility';
import {
  DefaultPortfolioDiversification,
  getDefaultDiversification,
} from './diversificationCategories.utility';
import { PortfolioDiversificationFormState } from './usePortfolioDiversificationForm';
import { getPortfolioDiversificationIsValid } from './portfolioDiversificationValidation';
import { DiversificationCategoryIdNumber } from '@/utils/enums';

interface ManualPortfolioDiversificationFormProps {
  onFormChange: (state: PortfolioDiversificationFormState) => void;
  portfolioDiversificationForm: PortfolioDiversificationFormState;
  realEstateDetailsForm: RealEstateDetailsFormState;
  selectedClassification: ClassificationItem | null;
}

const CLASSIFICATION_CATEGORY_NAME = 'Classification';

const sameScheme = (
  left: DiversificationScheme | null,
  right: DiversificationScheme | null,
) => left?.DiversificationId === right?.DiversificationId;

const sameCategory = (
  left: DiversificationCategory | null,
  right: DiversificationCategory | null,
) => left?.DiversificationCategoryId === right?.DiversificationCategoryId;

const getFallbackCategoryName = (categoryId: number) =>
  categoryId === DiversificationCategoryIdNumber.CLASSIFICATION
    ? CLASSIFICATION_CATEGORY_NAME
    : `Category ${categoryId}`;

const ManualPortfolioDiversificationForm = ({
  onFormChange,
  selectedClassification,
  realEstateDetailsForm,
  portfolioDiversificationForm,
}: ManualPortfolioDiversificationFormProps) => {
  const { t } = useLocalization();
  const isNativeApp = isCapacitorApp();

  const { allDiversifications, allDiversificationsLoading } =
    usePortfolioDiversificationCategories();
  const { data: diversificationCategoriesData } =
    useGetDiversificationCategoriesQuery();
  const { data: advisorGroupSettings } = useGetAdvisorGroupSettingsQuery();

  const [selectedCategory, setSelectedCategory] =
    useState<DiversificationScheme | null>(
      portfolioDiversificationForm.values.Category,
    );
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<DiversificationScheme | null>(
      portfolioDiversificationForm.values.SubCategory,
    );
  const [selectedSyntheticGroup, setSelectedSyntheticGroup] =
    useState<DiversificationCategory | null>(null);
  const [selectedDetail, setSelectedDetail] =
    useState<DiversificationScheme | null>(
      portfolioDiversificationForm.values.Detail,
    );
  const initializedRef = useRef(false);

  const predeterminedDiversification: DefaultPortfolioDiversification | null =
    useMemo(() => {
      if (!advisorGroupSettings || !selectedClassification) {
        return null;
      }

      const shouldHideClassifications = AdvisorGroupSettings(
        advisorGroupSettings,
      ).getByKey('HideDefaultClassifications');

      if (shouldHideClassifications) {
        return null;
      }

      const realEstateSubType = realEstateDetailsForm.values.AccountSubType
        ? Number(realEstateDetailsForm.values.AccountSubType)
        : null;

      return getDefaultDiversification(
        selectedClassification.ClassificationId,
        realEstateSubType,
      );
    }, [
      advisorGroupSettings,
      realEstateDetailsForm.values.AccountSubType,
      selectedClassification,
    ]);

  const allDiversificationOptions = useMemo(() => {
    if (!allDiversifications) {
      return [];
    }

    return sortAndDeduplicateByName(allDiversifications.Records);
  }, [allDiversifications]);

  const diversificationSchemeMap = useMemo(
    () =>
      new Map(
        allDiversificationOptions.map((option) => [
          option.DiversificationId,
          option,
        ]),
      ),
    [allDiversificationOptions],
  );

  const diversificationCategoryMap = useMemo(
    () =>
      new Map(
        (diversificationCategoriesData?.Records ?? []).map((category) => [
          category.DiversificationCategoryId,
          category,
        ]),
      ),
    [diversificationCategoriesData],
  );

  const rootDiversificationId = useMemo(() => {
    if (allDiversificationOptions.length === 0) {
      return null;
    }

    const explicitRoot =
      allDiversificationOptions.find((option) => option.ParentId == null)
        ?.DiversificationId ?? null;
    if (explicitRoot !== null) {
      return explicitRoot;
    }

    const allIds = new Set(
      allDiversificationOptions.map((option) => option.DiversificationId),
    );

    return (
      allDiversificationOptions.find(
        (option) => option.ParentId === 0 || !allIds.has(option.ParentId),
      )?.DiversificationId ?? null
    );
  }, [allDiversificationOptions]);

  const topLevelCategories = useMemo(() => {
    if (rootDiversificationId == null) {
      return [];
    }

    return sortAndDeduplicateByName(
      allDiversificationOptions.filter(
        (option) => option.ParentId === rootDiversificationId,
      ),
    );
  }, [allDiversificationOptions, rootDiversificationId]);

  const childOptions = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    return sortAndDeduplicateByName(
      allDiversificationOptions.filter(
        (option) => option.ParentId === selectedCategory.DiversificationId,
      ),
    );
  }, [allDiversificationOptions, selectedCategory]);

  const childOptionChildrenMap = useMemo(
    () =>
      new Map(
        childOptions.map((option) => [
          option.DiversificationId,
          sortAndDeduplicateByName(
            allDiversificationOptions.filter(
              (childOption) =>
                childOption.ParentId === option.DiversificationId,
            ),
          ),
        ]),
      ),
    [allDiversificationOptions, childOptions],
  );

  const selectedCategoryApiGroups = useMemo(
    () =>
      !selectedCategory
        ? []
        : (diversificationCategoriesData?.Records ?? [])
            .filter(
              (category) =>
                category.ParentDiversificationId ===
                selectedCategory.DiversificationId,
            )
            .sort(
              (left, right) =>
                left.DiversificationCategoryId -
                right.DiversificationCategoryId,
            ),
    [diversificationCategoriesData, selectedCategory],
  );

  const usesTreeRelationships = useMemo(
    () =>
      categoryUsesTreeRelationships({
        category: selectedCategory,
        options: allDiversificationOptions,
        categories: diversificationCategoriesData?.Records ?? [],
      }),
    [
      allDiversificationOptions,
      diversificationCategoriesData,
      selectedCategory,
    ],
  );

  const selectedSyntheticGroupChildren = useMemo(() => {
    if (!selectedSyntheticGroup) {
      return [];
    }

    return childOptions.filter(
      (option) =>
        option.DiversificationCategoryId ===
        selectedSyntheticGroup.DiversificationCategoryId,
    );
  }, [childOptions, selectedSyntheticGroup]);

  const syntheticGroups = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const apiGroups = selectedCategoryApiGroups;

    if (apiGroups.length > 0) {
      return apiGroups;
    }

    const uniqueCategoryIds = Array.from(
      new Set(
        childOptions
          .map((option) => option.DiversificationCategoryId)
          .filter(
            (categoryId): categoryId is number =>
              categoryId != null && diversificationCategoryMap.has(categoryId),
          ),
      ),
    ).sort((left, right) => left - right);

    return uniqueCategoryIds.map((categoryId) => ({
      DiversificationCategoryId: categoryId,
      Name:
        diversificationCategoryMap.get(categoryId)?.Name ??
        getFallbackCategoryName(categoryId),
      ParentDiversificationId: selectedCategory.DiversificationId,
    }));
  }, [
    childOptions,
    diversificationCategoryMap,
    selectedCategory,
    selectedCategoryApiGroups,
  ]);

  const detailOptions = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    if (usesTreeRelationships) {
      if (!selectedSubCategory) {
        return [];
      }

      return (
        childOptionChildrenMap.get(selectedSubCategory.DiversificationId) ?? []
      );
    }

    if (syntheticGroups.length === 0) {
      return childOptions;
    }

    if (!selectedSyntheticGroup) {
      return [];
    }

    const grandchildOptions = sortAndDeduplicateByName(
      allDiversificationOptions.filter((option) =>
        selectedSyntheticGroupChildren.some(
          (childOption) => option.ParentId === childOption.DiversificationId,
        ),
      ),
    );

    if (grandchildOptions.length > 0) {
      return grandchildOptions;
    }

    return selectedSyntheticGroupChildren;
  }, [
    allDiversificationOptions,
    childOptions,
    selectedCategory,
    selectedSyntheticGroup,
    selectedSyntheticGroupChildren,
    syntheticGroups.length,
    selectedSubCategory,
    usesTreeRelationships,
    childOptionChildrenMap,
  ]);

  useEffect(() => {
    if (initializedRef.current || allDiversificationOptions.length === 0) {
      return;
    }

    const initialCategory =
      portfolioDiversificationForm.values.Category ??
      allDiversificationOptions.find(
        (option) =>
          option.DiversificationId ===
          predeterminedDiversification?.DiversificationId,
      ) ??
      null;

    const initialSubCategory =
      portfolioDiversificationForm.values.SubCategory ??
      allDiversificationOptions.find(
        (option) =>
          option.DiversificationId ===
          predeterminedDiversification?.ChildDiversificationId,
      ) ??
      null;

    const initialDetail = portfolioDiversificationForm.values.Detail ?? null;

    const initialSyntheticGroupCategoryId =
      initialDetail?.DiversificationCategoryId ??
      diversificationSchemeMap.get(
        initialDetail?.ParentId ?? initialSubCategory?.DiversificationId ?? 0,
      )?.DiversificationCategoryId;

    const initialSyntheticGroup =
      initialSyntheticGroupCategoryId != null
        ? (diversificationCategoryMap.get(initialSyntheticGroupCategoryId) ?? {
            DiversificationCategoryId: initialSyntheticGroupCategoryId,
            Name: getFallbackCategoryName(initialSyntheticGroupCategoryId),
            ParentDiversificationId:
              initialCategory?.DiversificationId ?? rootDiversificationId ?? 0,
          })
        : null;

    if (!sameScheme(selectedCategory, initialCategory)) {
      setSelectedCategory(initialCategory);
    }

    if (!sameScheme(selectedDetail, initialDetail)) {
      setSelectedDetail(initialDetail);
    }

    if (!sameScheme(selectedSubCategory, initialSubCategory)) {
      setSelectedSubCategory(initialSubCategory);
    }

    if (!sameCategory(selectedSyntheticGroup, initialSyntheticGroup)) {
      setSelectedSyntheticGroup(initialSyntheticGroup);
    }

    initializedRef.current = true;
  }, [
    allDiversificationOptions,
    diversificationCategoryMap,
    diversificationSchemeMap,
    portfolioDiversificationForm.values.Category,
    portfolioDiversificationForm.values.Detail,
    portfolioDiversificationForm.values.SubCategory,
    predeterminedDiversification,
    rootDiversificationId,
    selectedCategory,
    selectedDetail,
    selectedSubCategory,
    selectedSyntheticGroup,
  ]);

  useEffect(() => {
    if (
      selectedCategory &&
      !topLevelCategories.some(
        (option) =>
          option.DiversificationId === selectedCategory.DiversificationId,
      )
    ) {
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSelectedSyntheticGroup(null);
      setSelectedDetail(null);
    }
  }, [selectedCategory, topLevelCategories]);

  useEffect(() => {
    if (!usesTreeRelationships) {
      return;
    }

    if (
      selectedSubCategory &&
      !childOptions.some(
        (option) =>
          option.DiversificationId === selectedSubCategory.DiversificationId,
      )
    ) {
      setSelectedSubCategory(null);
      setSelectedDetail(null);
    }
  }, [childOptions, selectedSubCategory, usesTreeRelationships]);

  useEffect(() => {
    if (usesTreeRelationships) {
      return;
    }

    if (
      selectedSyntheticGroup &&
      !syntheticGroups.some(
        (option) =>
          option.DiversificationCategoryId ===
          selectedSyntheticGroup.DiversificationCategoryId,
      )
    ) {
      setSelectedSyntheticGroup(null);
      setSelectedDetail(null);
    }
  }, [selectedSyntheticGroup, syntheticGroups, usesTreeRelationships]);

  useEffect(() => {
    if (
      selectedDetail &&
      !detailOptions.some(
        (option) =>
          option.DiversificationId === selectedDetail.DiversificationId,
      )
    ) {
      setSelectedDetail(null);
    }
  }, [detailOptions, selectedDetail]);

  useEffect(() => {
    const nextSubCategory = usesTreeRelationships ? selectedSubCategory : null;
    const requiresSubCategory =
      usesTreeRelationships && childOptions.length > 0;
    const isValid = !portfolioDiversificationForm.showDiversifications
      ? true
      : usesTreeRelationships
        ? selectedCategory != null &&
          (!requiresSubCategory || nextSubCategory != null) &&
          (detailOptions.length === 0 || selectedDetail != null)
        : getPortfolioDiversificationIsValid({
            selectedCategory,
            syntheticGroups,
            selectedSyntheticGroup,
            detailOptions,
            selectedDetail,
          });

    if (
      sameScheme(
        selectedCategory,
        portfolioDiversificationForm.values.Category,
      ) &&
      sameScheme(
        portfolioDiversificationForm.values.SubCategory,
        nextSubCategory,
      ) &&
      sameScheme(portfolioDiversificationForm.values.Detail, selectedDetail) &&
      portfolioDiversificationForm.isValid === isValid
    ) {
      return;
    }

    onFormChange({
      ...portfolioDiversificationForm,
      isValid,
      values: {
        Category: selectedCategory,
        SubCategory: nextSubCategory,
        Detail: selectedDetail,
      },
    });
  }, [
    onFormChange,
    portfolioDiversificationForm,
    selectedCategory,
    selectedDetail,
    selectedSyntheticGroup,
    syntheticGroups,
    detailOptions,
    selectedSubCategory,
    childOptions.length,
    usesTreeRelationships,
  ]);

  if (allDiversificationsLoading) {
    return (
      <Skeleton width='100%'>
        <Autocomplete
          renderInput={() => <TextField>Loading</TextField>}
          options={[]}
          sx={{ padding: '8px' }}
        />
      </Skeleton>
    );
  }

  if (allDiversificationOptions.length === 0) {
    return <></>;
  }

  return (
    <Grid
      container
      spacing={4}
    >
      {topLevelCategories.length > 0 && (
        <Autocomplete
          disableClearable={selectedCategory !== null}
          options={topLevelCategories}
          getOptionLabel={(option) => option.Name}
          getOptionKey={(option) => option.DiversificationId}
          isOptionEqualToValue={(option, value) =>
            option.DiversificationId === value.DiversificationId
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('addAccountModal.portfolioDiversification.category')}
              required={portfolioDiversificationForm.showDiversifications}
              slotProps={{
                htmlInput: { ...params.inputProps, readOnly: isNativeApp },
              }}
            />
          )}
          fullWidth
          onChange={(_event, value) => {
            setSelectedCategory(value);
            setSelectedSubCategory(null);
            setSelectedSyntheticGroup(null);
            setSelectedDetail(null);
          }}
          value={selectedCategory}
        />
      )}

      {usesTreeRelationships && childOptions.length > 0 && (
        <Autocomplete
          options={childOptions}
          getOptionLabel={(option) => option.Name}
          getOptionKey={(option) => option.DiversificationId}
          isOptionEqualToValue={(option, value) =>
            option.DiversificationId === value.DiversificationId
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('addAccountModal.portfolioDiversification.subCategory')}
              required={
                portfolioDiversificationForm.showDiversifications &&
                usesTreeRelationships &&
                childOptions.length > 0
              }
              slotProps={{
                htmlInput: { ...params.inputProps, readOnly: isNativeApp },
              }}
            />
          )}
          fullWidth
          onChange={(_event, value) => {
            setSelectedSubCategory(value);
            setSelectedDetail(null);
          }}
          value={selectedSubCategory}
        />
      )}

      {!usesTreeRelationships && syntheticGroups.length > 0 && (
        <Autocomplete
          options={syntheticGroups}
          getOptionLabel={(option) => option.Name}
          getOptionKey={(option) => option.DiversificationCategoryId}
          isOptionEqualToValue={(option, value) =>
            option.DiversificationCategoryId === value.DiversificationCategoryId
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('addAccountModal.portfolioDiversification.subCategory')}
              slotProps={{
                htmlInput: { ...params.inputProps, readOnly: isNativeApp },
              }}
            />
          )}
          fullWidth
          onChange={(_event, value) => {
            setSelectedSyntheticGroup(value);
            setSelectedDetail(null);
          }}
          value={selectedSyntheticGroup}
        />
      )}

      {detailOptions.length > 0 && (
        <Autocomplete
          options={detailOptions}
          getOptionLabel={(option) => option.Name}
          getOptionKey={(option) => option.DiversificationId}
          isOptionEqualToValue={(option, value) =>
            option.DiversificationId === value.DiversificationId
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('addAccountModal.portfolioDiversification.detail')}
              slotProps={{
                htmlInput: { ...params.inputProps, readOnly: isNativeApp },
              }}
            />
          )}
          fullWidth
          onChange={(_event, value) => setSelectedDetail(value)}
          value={selectedDetail}
        />
      )}
    </Grid>
  );
};

export default ManualPortfolioDiversificationForm;
